import React, { Suspense, useState } from 'react';
import PropTypes from 'prop-types';
import PrimaryButtonComponent from '../../../Components/Buttons/PrimaryButton/PrimaryButton.component';
import {
  httpEmailCourseInfo,
  httpBuyCourse,
} from '../../../Hooks/courses.hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormBackground } from '../../../AdminRoutes/CreateRoutes/CreateForm.styles';

const AlreadyPurchasedCourses = ({ courses, userId, userName, email }) => {
  // console.log('Already purchased courses', courses);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [status, setStatus] = useState('');
  const [coursePurchased, setCoursePurchased] = useState(false);
  const [courseSent, setCourseSent] = useState('');
  const queryClient = useQueryClient();

  const purchaseCourseMutation = useMutation({
    mutationFn: (course) => httpBuyCourse(course),
    onSuccess: async (data) => {
      const message = await data.json();
      queryClient.invalidateQueries(['users', 'user']);
      setCoursePurchased(true);
      setStatus(message.msg || 'Course has been purchased');
    },
    onError: async (error) => {
      const err = await error.json();
      console.error('Error purchasing course:', error);
      setStatus(err || 'Failed to purchase course for user');
    },
  });

  const sendEmailMutation = useMutation({
    mutationFn: ({ email, selectedCourse, userName, userId }) =>
      httpEmailCourseInfo(email, selectedCourse, userName, userId),
    onSuccess: async (data) => {
      const message = await data.json();
      queryClient.invalidateQueries(['users', 'user']);
      setCourseSent(message.msg || 'Email has been sent out');
    },
    onError: async (error) => {
      const err = await error.json();
      console.error('Error sending email:', error);
      setCourseSent(err || 'Failed to send out email');
    },
  });

  const handlePurchaseCourse = async (e) => {
    e.preventDefault();
    setStatus('Purchasing course...');
    const course = { courseId: selectedCourse._id, userId };
    purchaseCourseMutation.mutate(course);
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setCourseSent('Sending email...');
    sendEmailMutation.mutate({
      email,
      selectedCourse: selectedCourse.courseName,
      userName,
      userId,
    });
  };

  return (
    <FormBackground>
      <h4>
        Assign a course{' '}
        <span className="italicSpan">(all courses available)</span>
      </h4>
      <form
        onSubmit={handlePurchaseCourse}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          alignItems: 'center',
        }}
      >
        <div className="inputGroup" style={{ flex: 1, minWidth: 180 }}>
          <select
            id="courses"
            value={selectedCourse ? JSON.stringify(selectedCourse) : ''}
            onChange={(e) => setSelectedCourse(JSON.parse(e.target.value))}
            required
          >
            <option value="">Select Course</option>
            {(Array.isArray(courses) ? courses : []).map((course) => (
              <option key={course?._id} value={JSON.stringify(course)}>
                {course?.courseName}
              </option>
            ))}
          </select>
        </div>
        <div className="buttonGroup" style={{ minWidth: 120 }}>
          <Suspense fallback={<p>Loading button...</p>}>
            <PrimaryButtonComponent
              functionName={handlePurchaseCourse}
              span="Buy Course"
            />
          </Suspense>
        </div>
        {coursePurchased && (
          <div className="buttonGroup" style={{ minWidth: 120 }}>
            <Suspense fallback={<p>Loading button...</p>}>
              <PrimaryButtonComponent
                functionName={handleSendEmail}
                span="Send Course Email"
              />
            </Suspense>
          </div>
        )}
      </form>
      {status && <p className="statusMessage">{status}</p>}
      {courseSent && <p className="statusMessage">{courseSent}</p>}
    </FormBackground>
  );
};

AlreadyPurchasedCourses.propTypes = {
  courses: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      courseName: PropTypes.string.isRequired,
    })
  ).isRequired,
  userId: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
};

export default AlreadyPurchasedCourses;
